import type { ReactElement } from "react";
import {
  type BillingConfig,
  type DeploymentManifest,
  type OfferingHttpRoute,
  type ProductManifest,
  type ProductOfferingPlugin,
} from "@constructorfleet/offerings-sdk";
import {
  loadBhgCallAdminSettings,
  saveBhgCallAdminSettings,
  type BhgCallAdminSettings,
  type BhgCallSettings,
  type BhgPhraseTrigger,
  type BhgResultTrigger,
  type BhgSettingsSaveResult,
  type ProductDbClient,
  DEFAULT_BHG_CALL_SETTINGS,
  DEFAULT_BHG_PHRASE_TRIGGERS,
  DEFAULT_BHG_RESULT_TRIGGERS,
  loadBhgCallSettings,
  loadBhgPhraseRules,
  loadBhgResultRules,
  normalizeBhgCallSettings,
  normalizeBhgPhraseRules,
  normalizeBhgResultRules,
  saveBhgCallSettings,
  saveBhgPhraseRules,
  saveBhgResultRules,
} from "@constructorfleet/bhg-product-core";
import { bhgCallCatalog } from "@constructorfleet/bhg-product-core";
import { BhgCallAdminSettingsPage } from "@constructorfleet/bhg-product-ui";
import { BhgCallProductPage } from "@constructorfleet/bhg-product-ui";
import { BhgCallSignupPage } from "@constructorfleet/bhg-product-ui";
import {
  BhgCallUserDashboardPageV2,
  liveBhgCallDashboardProvider,
} from "@constructorfleet/bhg-product-ui";
import { BhgCallDemoDashboardPage } from "@constructorfleet/bhg-product-ui";
import { offeringHttpRoutes } from "./http-routes.mjs";
import { BHG_OFFERING_ID } from "@constructorfleet/bhg-product-core";
import { BHG_ROUTES } from "@constructorfleet/bhg-product-core";
import { bhgCallDeployment } from "@constructorfleet/bhg-product-core";
import { bhgCallPublicApiEndpoints } from "@constructorfleet/bhg-product-core";

type BhgCallProductMetadata = {
  manifest: ProductManifest;
  billing: BillingConfig;
  deployment: DeploymentManifest;
};

const bhgCallProductPlugin: ProductOfferingPlugin<
  ProductDbClient,
  BhgCallAdminSettings,
  BhgSettingsSaveResult,
  ReactElement
> &
  BhgCallProductMetadata = {
  id: BHG_OFFERING_ID,
  name: bhgCallCatalog.name,
  marketing: {
    ...bhgCallCatalog.marketing,
  },
  routes: {
    productPath: BHG_ROUTES.PRODUCT,
    adminSettingsPath: "admin/apps/bhg-call",
    signupPath: BHG_ROUTES.SIGNUP,
    demoPath: BHG_ROUTES.DEMO,
  },
  httpRoutes: offeringHttpRoutes as OfferingHttpRoute[],
  adminNav: {
    navLabel: "BHG Check-in Assist",
    adminLinkLabel: "Open BHG Check-in Assist settings",
  },
  manifest: {
    ...bhgCallCatalog.manifest,
  },
  billing: {
    ...bhgCallCatalog.billing,
  },
  deployment: {
    ...bhgCallDeployment,
  },
  loadSettings: loadBhgCallAdminSettings,
  saveSettings: saveBhgCallAdminSettings,
  adminUI: (dbClient) => <BhgCallAdminSettingsPage dbClient={dbClient} />,
  productPage: () => <BhgCallProductPage />,
  dashboardPage: (dbClient) => {
    const isAdmin = Boolean((dbClient as { isAdmin?: boolean }).isAdmin);
    const provider = isAdmin
      ? {
          ...liveBhgCallDashboardProvider,
          fetchSubscription: async () => {
            const real = await liveBhgCallDashboardProvider.fetchSubscription();
            if (real) return real;
            return { products: [BHG_OFFERING_ID], bhgEnrollment: null };
          },
        }
      : undefined;
    return <BhgCallUserDashboardPageV2 provider={provider} />;
  },
  signupPage: () => <BhgCallSignupPage />,
  demoPage: () => <BhgCallDemoDashboardPage />,
  publicApiEndpoints: bhgCallPublicApiEndpoints,
};

export default bhgCallProductPlugin;
export { bhgCallProductPlugin };

export {
  type BhgCallAdminSettings,
  type BhgCallSettings,
  type BhgPhraseTrigger,
  type BhgResultTrigger,
  type BhgSettingsSaveResult,
  type ProductDbClient,
  DEFAULT_BHG_CALL_SETTINGS,
  DEFAULT_BHG_PHRASE_TRIGGERS,
  DEFAULT_BHG_RESULT_TRIGGERS,
  loadBhgCallAdminSettings,
  loadBhgCallSettings,
  loadBhgPhraseRules,
  loadBhgResultRules,
  normalizeBhgCallSettings,
  normalizeBhgPhraseRules,
  normalizeBhgResultRules,
  saveBhgCallAdminSettings,
  saveBhgCallSettings,
  saveBhgPhraseRules,
  saveBhgResultRules,
};
